import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BellIcon } from "@heroicons/react/24/outline";
import { EC } from "../utils/const/ecommerce-colors-const";
import { sharedClearData } from "../utils/shared-functions";
import { sharedNextPage } from "../utils/shared-functions";
import LanguageDropdown from "./LanguageDropdown";
import { APP_LANGUAGES } from "../utils/i18n/app-languages";
import { useAppLanguage } from "../utils/i18n/use-app-language";

export default function LayoutHeader({ userInfo }) {
    const { t } = useTranslation();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const menuRef = useRef(null);
    const history = useHistory();
    const {
        selectedLanguage,
        setSelectedLanguage,
        isLanguageDropdownOpen,
        setIsLanguageDropdownOpen,
    } = useAppLanguage();

    useEffect(() => {
        // console.log("userInfo");
        // console.log(userInfo);
    }, [userInfo]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        if (showUserMenu) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showUserMenu]);

    const handleLogout = () => {
        setShowUserMenu(false);
        sharedClearData();
        sharedNextPage("/login", {}, history);
    };

    const handleGoToProfile = () => {
        setShowUserMenu(false);
        sharedNextPage("/profile", {}, history);
    };

    const getUserInitials = () => {
        if (!userInfo || !userInfo.nickname) return "U";
        return userInfo.nickname.substring(0, 2).toUpperCase();
    };

    return (
        <header className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/90 backdrop-blur-md shadow-sm">
            <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-14 sm:h-16">
                <div className="flex items-center gap-4 min-w-0 pl-10 lg:pl-0">
                    <button
                        type="button"
                        onClick={() => sharedNextPage("/landing", {}, history)}
                        className={`hidden sm:flex items-center gap-2 font-extrabold text-lg tracking-tight transition shrink-0 ${EC.brand.textLg}`}
                    >
                        <span className="text-xl shrink-0" aria-hidden>🛒</span>
                        <span className="truncate">{t("ct_brand")}</span>
                    </button>
                    {userInfo?.usuario && (
                        <p className="text-sm font-medium text-slate-500 hidden md:block truncate border-l border-slate-200 pl-4">
                            {t("ct_header_greeting", { name: userInfo.usuario })}
                        </p>
                    )}
                </div>

                <div className="flex items-center gap-2 sm:gap-3">
                    <LanguageDropdown
                        languages={APP_LANGUAGES}
                        selectedLanguage={selectedLanguage}
                        setSelectedLanguage={setSelectedLanguage}
                        isOpen={isLanguageDropdownOpen}
                        setIsOpen={setIsLanguageDropdownOpen}
                    />

                    <button
                        type="button"
                        className="relative p-2.5 text-slate-500 hover:text-ct-primary-600 hover:bg-ct-primary-50 rounded-xl transition-colors"
                        aria-label="Notifications"
                    >
                        <BellIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                        <span className="absolute top-2 right-2 h-2 w-2 bg-ct-price-500 rounded-full ring-2 ring-white" />
                    </button>

                    <div className="relative" ref={menuRef}>
                        <button
                            type="button"
                            onClick={() => setShowUserMenu(!showUserMenu)}
                            className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-1.5 rounded-xl hover:bg-ct-primary-50 border border-transparent hover:border-ct-primary-100 transition"
                            aria-expanded={showUserMenu}
                            aria-haspopup="menu"
                        >
                            <div className="w-9 h-9 bg-gradient-to-br from-ct-primary-600 to-ct-promo-500 rounded-xl flex items-center justify-center shadow-sm shadow-ct-primary-600/20">
                                <span className="text-white font-semibold text-sm">
                                    {getUserInitials()}
                                </span>
                            </div>
                            <div className="hidden sm:block text-left min-w-0">
                                <p className="text-sm font-semibold text-slate-900 truncate max-w-[140px] md:max-w-[200px]">
                                    {userInfo?.nombreEmpresa || t("label_nombre_empresa")}
                                </p>
                                <p className="text-xs text-slate-500 truncate max-w-[140px] md:max-w-[200px]">
                                    {userInfo?.usuario || "user"}
                                </p>
                            </div>
                            <svg
                                className={`w-4 h-4 text-slate-500 flex-shrink-0 transition-transform ${showUserMenu ? "rotate-180" : ""}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {showUserMenu && (
                            <div
                                className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-slate-200 py-2 z-[9999]"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                role="menu"
                            >
                                <div className="px-4 py-3 border-b border-slate-100">
                                    <p className="text-sm font-semibold text-slate-900 truncate">
                                        {userInfo?.usuario || t("label_usuario")}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-0.5 truncate">
                                        {userInfo?.email || "admin@comprartodo.com"}
                                    </p>
                                </div>

                                <div className="py-1">
                                    <button
                                        onClick={handleGoToProfile}
                                        type="button"
                                        className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-ct-primary-50 hover:text-ct-primary-700 transition-colors flex items-center gap-3"
                                    >
                                        <svg className={`w-5 h-5 ${EC.icon.primary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        <span className="font-medium">{t("label_mi_perfil")}</span>
                                    </button>

                                    <button
                                        onClick={() => {
                                            setShowUserMenu(false);
                                            history.push("/configuracion");
                                        }}
                                        type="button"
                                        className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-ct-primary-50 hover:text-ct-primary-700 transition-colors flex items-center gap-3"
                                    >
                                        <svg className={`w-5 h-5 ${EC.icon.primary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <span className="font-medium">{t("label_configuracion")}</span>
                                    </button>

                                    <button
                                        onClick={() => {
                                            setShowUserMenu(false);
                                            history.push("/landing");
                                        }}
                                        type="button"
                                        className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-ct-primary-50 hover:text-ct-primary-700 transition-colors flex items-center gap-3"
                                    >
                                        <span className="text-lg" aria-hidden>🛒</span>
                                        <span className="font-medium">{t("label_volver_al_inicio")}</span>
                                    </button>

                                    <div className="border-t border-slate-100 my-1" />

                                    <button
                                        onClick={handleLogout}
                                        type="button"
                                        className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-3"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                        </svg>
                                        <span className="font-medium">{t("label_cerrar_sesion")}</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
}
